<script setup lang="ts">
import { ChevronRight } from '~/components/icons'
import BreadcrumbNav from '../components/BreadcrumbNav.vue'
import ComingSoonContent from '../components/ComingSoonContent.vue'
import ContactSupportSection from '../components/ContactSupportSection.vue'
import ContentToggleTabs from '../components/ContentToggleTabs.vue'
import HelpCenterSearch from '../components/HelpCenterSearch.vue'

definePageMeta({
  layout: 'pusat-bantuan',
})

const route = useRoute()
const categorySlug = computed(() => route.params.category as string)

const { getCategoryBySlug, getArticlesByCategory, searchArticles, tabs } = usePusatBantuanData()

const category = computed(() => getCategoryBySlug(categorySlug.value))

if (!category.value) {
  throw createError({ statusCode: 404, message: 'Kategori tidak ditemukan' })
}

useHead({
  title: `${category.value?.title} - Pusat Bantuan`,
})

const selectedTab = ref<'faq' | 'artikel-panduan' | 'video-panduan'>('faq')
const searchQuery = ref('')

const filteredArticles = computed(() => {
  if (searchQuery.value.trim()) {
    return searchArticles(searchQuery.value, categorySlug.value)
      .filter(a => a.tab === selectedTab.value)
  }
  return getArticlesByCategory(categorySlug.value, selectedTab.value)
})

const latestUpdate = computed(() => {
  const categoryArticles = getArticlesByCategory(categorySlug.value)
  return categoryArticles.length > 0 ? categoryArticles[0]!.updatedAt : '-'
})
</script>

<template>
  <div class="container mx-auto px-4 py-8 space-y-6">
    <Card class="p-6 space-y-5">
      <div class="space-y-4">
        <div class="flex items-start justify-between">
          <BreadcrumbNav
            :items="[
              { label: category?.title || '', to: `/pusat-bantuan/${categorySlug}` },
              { label: tabs.find(t => t.value === selectedTab)?.label || 'FAQ' },
            ]"
          />
          <span class="text-sm shrink-0">Update: {{ latestUpdate }}</span>
        </div>

        <div>
          <h1 class="text-xl font-semibold text-neutral-800">
            {{ category?.title }}
          </h1>
          <p class="text-sm text-neutral-500 mt-1">
            {{ category?.description }}
          </p>
        </div>

        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <ContentToggleTabs v-model="selectedTab" />
          <div class="w-full sm:w-64">
            <HelpCenterSearch
              v-model="searchQuery"
              placeholder="Cari artikel bantuan atau topik"
            />
          </div>
        </div>
      </div>

      <!-- Coming Soon for Artikel Panduan & Video Panduan -->
      <ComingSoonContent
        v-if="selectedTab === 'artikel-panduan' || selectedTab === 'video-panduan'"
        :type="selectedTab"
      />

      <!-- FAQ List -->
      <Card v-else class="mt-6 p-0">
        <div v-if="filteredArticles.length === 0" class="text-center py-8">
          <p class="text-sm text-neutral-400">
            Belum ada konten untuk tab ini.
          </p>
        </div>

        <div v-else class="py-2">
          <NuxtLink
            v-for="article in filteredArticles"
            :key="article.slug"
            :to="`/pusat-bantuan/${categorySlug}/${article.tab}/${article.slug}`"
            class="flex items-center justify-between w-full px-6 py-4 text-left hover:bg-neutral-50 transition-colors"
          >
            <span class="pr-4">{{ article.question }}</span>
            <ChevronRight class="size-6 text-primary shrink-0" />
          </NuxtLink>
        </div>
      </Card>
    </Card>

    <ContactSupportSection />
  </div>
</template>
